/* START_MODULE_CONTRACT
 * PURPOSE: Мини-диалог ввода фактических часов работы над задачей в конкретный день (UC-016).
 * SCOPE: create, edit, delete actual hours.
 * DEPENDS: M-FE-API
 * LINKS: M-FE-TIMELINE, M-TASK-ACTUAL-HOURS
 * END_MODULE_CONTRACT
 *
 * START_MODULE_MAP
 * EditActualHoursDialog - мини-модалка фактических часов
 * END_MODULE_MAP
 *
 * START_CHANGE_SUMMARY
 * LAST_CHANGE: 2026-07-03 - UC-016.
 * END_CHANGE_SUMMARY
 */

import { useEffect, useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import { useDialogFormReady } from '../../../hooks/useDialogFormReady';
import { formatHours } from '../timelineUtils';

/* START_METHOD_EditActualHoursDialog */
/* START_CONTRACT: EditActualHoursDialog
 * PURPOSE: Редактирование фактических часов за день; удаление возвращает к плану.
 * INPUTS: open, onClose, context, onSave, onDelete
 * OUTPUTS: JSX
 * SIDE_EFFECTS: onSave/onDelete HTTP via parent
 * LINKS: V-M-FE-TIMELINE
 * END_CONTRACT: EditActualHoursDialog
 */
export default function EditActualHoursDialog({
  open,
  onClose,
  context,
  onSave,
  onDelete,
}) {
  const firstFieldRef = useRef(null);
  const { dialogTransitionSlotProps } = useDialogFormReady({ firstFieldRef });
  const [hours, setHours] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!context) return;
    const initial = context.currentActual ?? context.plannedHours ?? 0;
    setHours(String(initial));
    setError('');
  }, [context]);

  if (!context) return null;

  const parsed = Number(hours);
  const isValid = hours !== '' && Number.isFinite(parsed) && parsed >= 0;
  const canSave = isValid && !loading;
  const canDelete = Boolean(context.recordId) && !loading;

  /* START_METHOD_handleSave */
  const handleSave = async () => {
    if (!canSave) return;
    setLoading(true);
    setError('');
    try {
      await onSave(parsed);
      console.info('[EditActualHoursDialog][handleSave][BLOCK_SAVE_OK]');
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Не удалось сохранить фактические часы');
    } finally {
      setLoading(false);
    }
  };
  /* END_METHOD_handleSave */

  /* START_METHOD_handleDelete */
  const handleDelete = async () => {
    if (!canDelete) return;
    setLoading(true);
    setError('');
    try {
      await onDelete();
      console.info('[EditActualHoursDialog][handleDelete][BLOCK_DELETE_OK]');
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Не удалось удалить запись');
    } finally {
      setLoading(false);
    }
  };
  /* END_METHOD_handleDelete */

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth slotProps={{ transition: dialogTransitionSlotProps }}>
      <DialogTitle sx={{ pb: 0.5, fontSize: '1rem' }}>
        Фактические часы
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
          {context.employeeName}
          {' · '}
          {context.featureName}
          {' · '}
          {context.workDate}
        </Typography>
        {context.plannedHours != null ? (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            План за день:
            {' '}
            {formatHours(context.plannedHours)}
          </Typography>
        ) : null}
        {error ? <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert> : null}
        <TextField
          inputRef={firstFieldRef}
          size="small"
          fullWidth
          type="number"
          label="Часы"
          inputProps={{ min: 0, step: 0.25 }}
          value={hours}
          onChange={(e) => setHours(e.target.value)}
          disabled={loading}
        />
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 1.5 }}>
        {canDelete ? (
          <Button size="small" color="error" onClick={handleDelete} disabled={loading}>
            Удалить
          </Button>
        ) : null}
        <Box sx={{ flex: 1 }} />
        <Button size="small" onClick={onClose} disabled={loading}>
          Отмена
        </Button>
        <Button size="small" variant="contained" onClick={handleSave} disabled={!canSave}>
          Сохранить
        </Button>
      </DialogActions>
    </Dialog>
  );
}
/* END_METHOD_EditActualHoursDialog */
